import { Ref, useEffect, useRef, useState } from 'react';

import { gtmService } from '~/services/gtm';

// eslint-disable-next-line no-undef
export const useElementOnScreen = (options?: IntersectionObserverInit) => {
  const containerRef = useRef<Element>();
  const [isVisible, setIsVisible] = useState(false);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisibleAfterTimeout, setIsVisibleAfterTimeout] = useState(false);

  // eslint-disable-next-line no-undef
  const callbackFn: IntersectionObserverCallback = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);

    if (entry.isIntersecting && (isVisibleAfterTimeout || timeoutId.current))
      return;

    if (entry.isIntersecting) {
      timeoutId.current = setTimeout(() => {
        setIsVisibleAfterTimeout(true);
        timeoutId.current = null;
      }, gtmService.minimumViewTime);
    } else {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
      setIsVisibleAfterTimeout(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef, options]);

  return {
    containerRef: containerRef as Ref<Element>,
    isVisible,
    isVisibleAfterTimeout,
  };
};
